import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { joinGroup, getArenaData } from '../storageService';
import './ArenaView.css';

const ArenaView = () => {
    const [status, setStatus] = useState('loading'); // loading, gatekeeper, active
    const [group, setGroup] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [activeTab, setActiveTab] = useState('weekly'); // weekly, streaks, scholars
    const [inviteCode, setInviteCode] = useState('');
    const [error, setError] = useState(null);
    const [userId, setUserId] = useState(null);
    const [lastWinner, setLastWinner] = useState(null);

    // 1. TOP LEVEL HOOKS (Must come first)
    const topScholar = useMemo(() => {
        if (!leaderboard || leaderboard.length === 0) return null;
        return [...leaderboard].sort((a, b) => b.badges - a.badges)[0];
    }, [leaderboard]);

    const sortedLeaderboard = useMemo(() => {
        const list = [...leaderboard];
        if (activeTab === 'streaks') return list.sort((a, b) => b.streak - a.streak || b.points - a.points);
        if (activeTab === 'scholars') return list.sort((a, b) => b.badges - a.badges || b.points - a.points);
        return list.sort((a, b) => b.points - a.points);
    }, [leaderboard, activeTab]);

    useEffect(() => {
        let userChannel;
        const init = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            setUserId(user.id);
            try {
                const data = await getArenaData(user.id);
                if (data.status === 'no_group') {
                    setStatus('gatekeeper');
                } else if (data.status === 'active') {
                    setGroup(data.group);
                    setLeaderboard(data.leaderboard);
                    setStatus('active');
                    userChannel = supabase
                        .channel('arena-live-updates')
                        .on('postgres_changes', { 
                            event: 'UPDATE', 
                            schema: 'public', 
                            table: 'users' 
                        }, (payload) => {
                            setLeaderboard(prev => {
                                const updated = prev.map(u => 
                                    u.id === payload.new.id 
                                    ? { ...u, points: payload.new.weekly_points, name: payload.new.display_name } 
                                    : u
                                );
                                return updated;
                            });
                        })
                        .subscribe();
                }
            } catch (err) {
                console.error(err);
                setError("Connection Failed");
            }
        };
        init();
        return () => { if (userChannel) supabase.removeChannel(userChannel); };
    }, []);

    const handleJoin = async (e) => {
        e.preventDefault();
        if (!inviteCode) return;
        setError(null);
        try {
            await joinGroup(userId, inviteCode.toUpperCase().trim());
            const data = await getArenaData(userId);
            setGroup(data.group);
            setLeaderboard(data.leaderboard);
            setLastWinner(data.lastWinner);
            setStatus('active');
        } catch (err) {
            setError(err.message);
        }
    };

    // 2. CONDITIONAL RENDER LOGIC (Must come after all hooks)
    if (status === 'loading') {
        return (
            <div className="arena-container" style={{display:'flex', justifyContent:'center', alignItems:'center'}}>
                <div className="loading-spinner"></div>
            </div>
        );
    }

    if (status === 'gatekeeper') {
        return (
            <div className="arena-container theme-aware">
                <div className="gatekeeper-wrapper">
                    <div className="gatekeeper-card">
                        <span className="material-symbols-outlined gatekeeper-icon">security</span>
                        <h2>The Arena is Locked</h2>
                        <p style={{color: 'var(--text-muted)'}}>Enter a cohort invite code to access the leaderboard.</p>
                        <form onSubmit={handleJoin}>
                            <input 
                                type="text" 
                                className="gatekeeper-input"
                                placeholder="INVITE CODE"
                                value={inviteCode}
                                onChange={(e) => setInviteCode(e.target.value)}
                                autoFocus
                            />
                            {error && <div className="error-banner" style={{marginBottom: '15px'}}>{error}</div>}
                            <button type="submit" className="btn full-width primary">Enter Arena</button>
                        </form>
                        <div style={{marginTop: '20px', fontSize: '0.9rem', color: 'var(--text-muted)'}}>
                            <p>If you are interested in starting a study group, let us know! Contact us at <strong>contact@walis.app</strong>.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const isLocked = leaderboard.length < 1;

    return (
        <div className="arena-container theme-aware">
            <header className="arena-header-refined">
                <div className="group-meta">
                    <div className="group-name-row">
                        <h2>{group?.name}</h2>
                        <span className="material-symbols-outlined verified-badge">verified</span>
                    </div>
                    <span className="member-count">{leaderboard.length} Members</span>
                </div>
                
                <div className="milestone-notice">
                    <span className="material-symbols-outlined notice-icon">
                        {lastWinner ? 'workspace_premium' : 'trending_up'}
                    </span>
                    <div className="notice-content">
                        <span className="notice-label">
                            {lastWinner ? 'Last Week\'s Champion' : 'Current Leader'}
                        </span>
                        <span className="notice-text">
                            {lastWinner 
                                ? `${lastWinner.name} • ${lastWinner.points} pts` 
                                : `${topScholar?.name || 'Counselor'} • ${topScholar?.badges || 0} Badges`
                            }
                        </span>
                    </div>
                </div>
            </header>

            <div className="arena-content" style={{position: 'relative'}}>
                {isLocked && (
                    <div className="arena-lock-overlay">
                        <div className="lock-content">
                            <span className="material-symbols-outlined" style={{fontSize: '4rem'}}>group_add</span>
                            <h3>Arena Locked</h3>
                            <p>Current: {leaderboard.length} / 10 Members</p>
                            <p className="sub">Invite your cohort to unlock the leaderboard.</p>
                            <div className="invite-box">Code: <strong>{group?.invite_code}</strong></div>
                        </div>
                    </div>
                )}

                <div className={`arena-main-ui ${isLocked ? 'blurred' : ''}`}>
                    <div className="arena-tabs">
                        <button 
                            className={`arena-tab ${activeTab === 'weekly' ? 'active' : ''}`} 
                            onClick={() => setActiveTab('weekly')}
                        >Weekly Challenge</button>
                        <button 
                            className={`arena-tab ${activeTab === 'streaks' ? 'active' : ''}`} 
                            onClick={() => setActiveTab('streaks')}
                        >Streaks</button>
                        <button 
                            className={`arena-tab ${activeTab === 'scholars' ? 'active' : ''}`} 
                            onClick={() => setActiveTab('scholars')}
                        >Scholars</button>
                    </div>

                    <div className="leaderboard-list">
                        {sortedLeaderboard.map((user, index) => {
                            const rank = index + 1;
                            let rankIcon = null;
                            if (rank === 1) rankIcon = <span className="material-symbols-outlined rank-icon">emoji_events</span>;
                            else if (rank === 2) rankIcon = <span className="material-symbols-outlined rank-icon">military_tech</span>;
                            else if (rank === 3) rankIcon = <span className="material-symbols-outlined rank-icon">workspace_premium</span>;

                            // Determine what value to show in the score column
                            const displayValue = activeTab === 'weekly' ? `${user.points} pts` : 
                                                activeTab === 'streaks' ? `${user.streak} days` : 
                                                `${user.badges} badges`;

                            return (
                                <div key={user.id} className={`lb-row rank-${rank} ${user.id === userId ? 'is-me' : ''}`}>
                                    <div className="lb-rank">
                                        {rankIcon || <span>{rank}</span>}
                                    </div>
                                    <div className="lb-name">{user.name}</div>
                                    <div className="lb-score">{displayValue}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ArenaView;